// src/lib/opportunity/formatPaymentMilestoneTypes.ts

import { FORMAT_CALC_PRODUCT_TYPE_WILDCARD } from "./formatCalcLineTypes.js";
import type { OpportunityFormatKey } from "../../types/opportunity.js";

export const FORMAT_PAYMENT_MILESTONE_ROLES = ["deposit", "custom", "balance"] as const;
export type FormatPaymentMilestoneRole = (typeof FORMAT_PAYMENT_MILESTONE_ROLES)[number];

export const FORMAT_PAYMENT_AMOUNT_MODES = [
  "percent_of_remaining",
  "percent_of_total",
  "fixed",
  "line_item_amount",
] as const;
export type FormatPaymentAmountMode = (typeof FORMAT_PAYMENT_AMOUNT_MODES)[number];

/** Sentinel ref for the base amount line in payment milestones. */
export const PAYMENT_LINE_ITEM_BASE_REF = "__base_amount__";

export const FORMAT_PAYMENT_LABEL_MAX_LEN = 200;

export type FormatPaymentMilestone = {
  id: string;
  organization_id: string;
  format_key: OpportunityFormatKey;
  product_type: string;
  milestone_role: FormatPaymentMilestoneRole;
  sort_order: number;
  label: string;
  subtext: string | null;
  amount_mode: FormatPaymentAmountMode | null;
  default_amount: number | null;
  line_item_ref: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type FormatPaymentMilestoneDef = Pick<
  FormatPaymentMilestone,
  | "milestone_role"
  | "sort_order"
  | "label"
  | "subtext"
  | "amount_mode"
  | "default_amount"
  | "line_item_ref"
>;

export const DEFAULT_DEPOSIT_MILESTONE: FormatPaymentMilestoneDef = {
  milestone_role: "deposit",
  sort_order: 0,
  label: "Deposit due at signing",
  subtext: "Secures scheduling",
  amount_mode: null,
  default_amount: null,
  line_item_ref: null,
};

export const DEFAULT_BALANCE_MILESTONE: FormatPaymentMilestoneDef = {
  milestone_role: "balance",
  sort_order: 9999,
  label: "Final balance due on delivery",
  subtext: "Remaining balance — due at project delivery",
  amount_mode: null,
  default_amount: null,
  line_item_ref: null,
};

export function defaultPaymentMilestones(): FormatPaymentMilestoneDef[] {
  return [DEFAULT_DEPOSIT_MILESTONE, DEFAULT_BALANCE_MILESTONE];
}

function sortMilestones(rows: FormatPaymentMilestoneDef[]): FormatPaymentMilestoneDef[] {
  return [...rows].sort((a, b) => a.sort_order - b.sort_order);
}

/** Product-specific milestones win; otherwise org `*` template; else built-in defaults. */
export function selectFormatPaymentMilestones(args: {
  milestones: FormatPaymentMilestone[];
  productType: string;
}): FormatPaymentMilestoneDef[] {
  const specific = args.milestones.filter((m) => m.product_type === args.productType);
  const wildcard = args.milestones.filter(
    (m) => m.product_type === FORMAT_CALC_PRODUCT_TYPE_WILDCARD,
  );
  const fromDb = specific.length > 0 ? specific : wildcard;
  if (fromDb.length === 0) return defaultPaymentMilestones();
  return sortMilestones(fromDb);
}

export function mergePaymentMilestonesForEditor(
  rows: FormatPaymentMilestone[],
): FormatPaymentMilestone[] {
  const sorted = [...rows].sort((a, b) => a.sort_order - b.sort_order);
  const hasDeposit = sorted.some((m) => m.milestone_role === "deposit");
  const hasBalance = sorted.some((m) => m.milestone_role === "balance");
  const merged: FormatPaymentMilestone[] = [...sorted];
  if (!hasDeposit) {
    merged.unshift({
      id: "__default_deposit__",
      organization_id: "",
      format_key: "generic",
      product_type: "",
      ...DEFAULT_DEPOSIT_MILESTONE,
      updated_by_user_id: null,
      created_at: "",
      updated_at: "",
    });
  }
  if (!hasBalance) {
    merged.push({
      id: "__default_balance__",
      organization_id: "",
      format_key: "generic",
      product_type: "",
      ...DEFAULT_BALANCE_MILESTONE,
      updated_by_user_id: null,
      created_at: "",
      updated_at: "",
    });
  }
  return merged.sort((a, b) => a.sort_order - b.sort_order);
}

export function isPersistedPaymentMilestoneId(id: string): boolean {
  return !id.startsWith("__default_");
}
